import { memo, useCallback } from "react";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import CloseIcon from "@mui/icons-material/Close";

import {
  AUTOMATION_DISPLAY_TITLES,
  AUTOMATION_TRIGGER_MOMENTS,
} from "../constants/automationUiConstants";
import { useAutomationForm } from "../hooks/useAutomationForm";
import type { AutomationContext, AutomationId } from "../types";
import AutomationForm from "./AutomationForm";

export interface AutomationDialogProps {
  automationId: AutomationId;
  context: AutomationContext;
  open: boolean;
  onClose: () => void;
  onAfterSaved: () => void;
}

const AutomationDialog = memo(
  ({
    automationId,
    context,
    open,
    onClose,
    onAfterSaved,
  }: AutomationDialogProps) => {
    const form = useAutomationForm({
      automationId,
      context,
      onSaved: () => {
        onAfterSaved();
        onClose();
      },
    });

    const handleClose = useCallback(() => {
      if (form.isSubmitting) return;
      onClose();
    }, [form.isSubmitting, onClose]);

    const automationTitle = AUTOMATION_DISPLAY_TITLES[automationId];
    const triggerMoment = AUTOMATION_TRIGGER_MOMENTS[automationId];
    const canSave = Boolean(form.selectedTemplateId?.trim()) && !form.isSubmitting;

    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
        <DialogTitle sx={{ pr: 6 }}>
          Configurar {automationTitle}
          <IconButton
            aria-label="Fechar"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {form.hasSubmitError && (
              <Alert severity="error">
                Erro ao salvar configuração da automação. Tente novamente.
              </Alert>
            )}
            <AutomationForm
              form={form}
              organizationId={context.organizationId}
              whatsappBusinessAccountId={context.whatsappBusinessAccountId}
              triggerMoment={triggerMoment}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="inherit"
            disabled={form.isSubmitting}
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => form.submit()}
            disabled={!canSave}
            sx={{ textTransform: "none" }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

AutomationDialog.displayName = "AutomationDialog";

export default AutomationDialog;
